import { isSupabaseConfigured } from '../../../lib/services';
import { supabase } from '../../../lib/supabase';
import type { InsurancePolicy, InsurancePolicyFormData } from '../types';

export const policyService = {
  async getAll(): Promise<InsurancePolicy[]> {
    if (!isSupabaseConfigured()) {
      const mockPolicies: InsurancePolicy[] = [
        {
          id: 'policy-1',
          patient_id: 'patient-1',
          policy_number: 'POL-001',
          insurance_company: 'HealthCare Plus',
          policy_type: 'individual',
          coverage_amount: 50000,
          deductible: 1000,
          copay_percentage: 20,
          effective_date: '2024-01-01',
          expiry_date: '2024-12-31',
          status: 'active',
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        }
      ];
      return mockPolicies;
    }
    
    const { data, error } = await supabase
      .from('insurance_policies')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data as InsurancePolicy[];
  },

  async getById(id: string): Promise<InsurancePolicy | null> {
    if (!isSupabaseConfigured()) {
      return {
        id,
        patient_id: 'patient-1',
        policy_number: 'POL-001',
        insurance_company: 'HealthCare Plus',
        policy_type: 'individual',
        coverage_amount: 50000,
        deductible: 1000,
        copay_percentage: 20,
        effective_date: '2024-01-01',
        expiry_date: '2024-12-31',
        status: 'active',
        created_at: '2024-01-01T00:00:00Z',
        updated_at: '2024-01-01T00:00:00Z'
      };
    }
    
    const { data, error } = await supabase
      .from('insurance_policies')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data as InsurancePolicy;
  },

  async create(policy: InsurancePolicyFormData): Promise<InsurancePolicy> {
    if (!isSupabaseConfigured()) {
      const mockPolicy: InsurancePolicy = {
        ...policy,
        id: `policy-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      return mockPolicy;
    }
    
    const { data, error } = await supabase
      .from('insurance_policies')
      .insert([{ ...policy, updated_at: new Date().toISOString() }])
      .select()
      .single();
    
    if (error) throw error;
    return data as InsurancePolicy;
  },

  async update(id: string, policy: Partial<InsurancePolicyFormData>): Promise<InsurancePolicy | null> {
    if (!isSupabaseConfigured()) {
      const existing = await this.getById(id);
      return {
        ...existing!,
        ...policy,
        updated_at: new Date().toISOString()
      };
    }
    
    const { data, error } = await supabase
      .from('insurance_policies')
      .update({ ...policy, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data as InsurancePolicy;
  },

  async getByPatient(patientId: string): Promise<InsurancePolicy[]> {
    if (!isSupabaseConfigured()) {
      return [];
    }
    
    const { data, error } = await supabase
      .from('insurance_policies')
      .select('*')
      .eq('patient_id', patientId)
      .eq('status', 'active');
    
    if (error) throw error;
    return data as InsurancePolicy[];
  }
};
